import { useState, useCallback } from 'react';
import format from 'date-fns/format';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import addMonths from 'date-fns/addMonths';
import useAxiosPrivate from './useAxiosPrivate';
import { exportMonthlyEnergyReport, exportEnergyReport } from '../services/excelExport';

/**
 * Hook för att exportera energidata till Excel
 * 
 * @returns {Object} - { exportMonthly, exportCustomRange, loading, error }
 */
const useEnergyExport = () => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Hämtar energidata från API
   */
  const fetchEnergyData = useCallback(async (meterId, fromDate, toDate, interval = 'month') => {
    const url = `/meters/${meterId}/metrics/energy?interval=${interval}&from=${format(
      fromDate,
      'yyyy-MM-dd'
    )}&to=${format(toDate, 'yyyy-MM-dd')}`;
    
    const response = await axiosPrivate.get(url, {
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });
    
    return response.data;
  }, [axiosPrivate]);

  /**
   * Exporterar månatlig energirapport för senaste 12 månaderna
   * 
   * @param {Object} meter - Mätarobjekt med id, name, identifier
   * @param {number} [months=12] - Antal månader tillbaka att inkludera
   */
  const exportMonthly = useCallback(async (meter, months = 12) => {
    if (!meter?.id) {
      setError('Ingen mätare angiven');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const toDate = endOfMonth(new Date());
      const fromDate = startOfMonth(addMonths(new Date(), -(months - 1)));
      
      const data = await fetchEnergyData(meter.id, fromDate, toDate, 'month');
      
      exportMonthlyEnergyReport({
        meter,
        data,
        fromDate,
        toDate
      });
    } catch (err) {
      console.error('Fel vid export:', err);
      setError(err.message || 'Kunde inte exportera rapport');
    } finally {
      setLoading(false);
    }
  }, [fetchEnergyData]);

  /**
   * Exporterar energirapport för anpassat datumintervall
   * 
   * @param {Object} meter - Mätarobjekt
   * @param {Date} fromDate - Startdatum
   * @param {Date} toDate - Slutdatum
   * @param {string} [interval='day'] - Intervall (hour, day, month, year)
   */
  const exportCustomRange = useCallback(async (meter, fromDate, toDate, interval = 'day') => {
    if (!meter?.id) {
      setError('Ingen mätare angiven');
      return;
    }

    if (!fromDate || !toDate) {
      setError('Startdatum och slutdatum måste anges');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await fetchEnergyData(meter.id, fromDate, toDate, interval);
      
      if (interval === 'month') {
        exportMonthlyEnergyReport({
          meter,
          data,
          fromDate,
          toDate
        });
      } else {
        exportEnergyReport({
          meter,
          data,
          fromDate,
          toDate,
          interval
        });
      }
    } catch (err) {
      console.error('Fel vid export:', err);
      setError(err.message || 'Kunde inte exportera rapport');
    } finally {
      setLoading(false);
    }
  }, [fetchEnergyData]);

  /**
   * Exporterar energirapport för specifik månad
   * 
   * @param {Object} meter - Mätarobjekt
   * @param {number} year - År (t.ex. 2024)
   * @param {number} month - Månad (1-12)
   */
  const exportSingleMonth = useCallback(async (meter, year, month) => {
    if (!meter?.id) {
      setError('Ingen mätare angiven');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fromDate = new Date(year, month - 1, 1);
      const toDate = endOfMonth(fromDate);
      
      // Hämta daglig data för månaden
      const data = await fetchEnergyData(meter.id, fromDate, toDate, 'day');
      
      exportEnergyReport({
        meter,
        data,
        fromDate,
        toDate,
        interval: 'day'
      });
    } catch (err) {
      console.error('Fel vid export:', err);
      setError(err.message || 'Kunde inte exportera rapport');
    } finally {
      setLoading(false);
    }
  }, [fetchEnergyData]);

  return {
    exportMonthly,
    exportCustomRange,
    exportSingleMonth,
    loading,
    error
  };
};

export default useEnergyExport;

